import { useAsyncData, useNuxtApp } from '#imports'

import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'

import { SALES_CUSTOMER_CARE_SCOPE } from '~/scopes/care-commons/utils/logger/salesCustomerCare/config'
import { useSalesCustomerCareLogger } from '~/scopes/care-commons/utils/logger/salesCustomerCare/useSalesCustomerCareLogger'

/**
 * Explanation:
 *
 * When using async composables or functions, there is a risk of losing the Nuxt context after an `async/await` operation.
 * This occurs because the Vue.js Composition API and Nuxt composables rely on an implicit context that gets unset
 * after an async operation to prevent shared references in a global context.
 *
 * Example:
 *
 * async setup() {
 *   const instance = getCurrentInstance(); // Works
 *   await someAsyncOperation(); // Vue unsets the context
 *   getCurrentInstance(); // null
 * }
 *
 * To address this, we use the `useNuxtApp` and `runWithContext` methods to restore the context, similar to how <script setup> works.
 *
 * Additionally, Nuxt has an experimental feature called `asyncContext` that will automatically handle the context for us.
 * This code can be updated once this feature is no longer experimental.
 *
 * For more details, see:
 * - Nuxt Documentation on asyncContext: https://nuxt.com/docs/api/composables/async-context
 * - GitHub Issue Discussion: https://github.com/nuxt/nuxt/issues/14269#issuecomment-1397352832
 */

export function useReturnConfirmationData({
  resolutionId,
}: {
  resolutionId: string
}) {
  const nuxtApp = useNuxtApp()
  const $httpFetch = useHttpFetch()
  const { logHttpError } = useSalesCustomerCareLogger()
  const getResolutionKey = getAsyncDataKey('return-confirmation-data', {
    resolutionId,
  })

  return useAsyncData(getResolutionKey, async () => {
    const resolutionPayload = await $httpFetch(
      '/resolution-engine/resolutions/:resolutionId',
      {
        pathParams: {
          resolutionId,
        },
        onResponseError: ({ response }) => {
          if (response) {
            logHttpError({
              // eslint-disable-next-line no-underscore-dangle
              error: response._data,
              errorStatusCode: response.status,
              errorTitle: `${SALES_CUSTOMER_CARE_SCOPE.returnConfirmationPage} Error when fetching associated resolution for resolutionId:${resolutionId}`,
            })
          }
        },
      },
    )

    const orderlineTimelinePayload = await nuxtApp.runWithContext(async () =>
      $httpFetch('/my-orders/orderlines/:orderlineId/timeline', {
        pathParams: {
          orderlineId: resolutionPayload.resolution.problemContext.orderline.id,
        },
        onResponseError: ({ response }) => {
          if (response) {
            logHttpError({
              // eslint-disable-next-line no-underscore-dangle
              error: response._data,
              errorStatusCode: response.status,
              errorTitle: `${SALES_CUSTOMER_CARE_SCOPE.returnConfirmationPage} Error when fetching associated orderline timeline for orderlineId:${resolutionPayload.resolution.problemContext.orderline.id}`,
            })
          }
        },
      }),
    )

    return {
      orderlineTimeline: orderlineTimelinePayload,
      resolution: resolutionPayload.resolution,
    }
  })
}
